import { Component, computed, inject, signal, TemplateRef, viewChild, ViewEncapsulation } from '@angular/core';
import { AsyncPipe, DatePipe, DecimalPipe, JsonPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { firstValueFrom, Observable } from 'rxjs';
import moment from 'moment';
import { AppState } from 'src/app/app.state';

import { AdminPayterLocationsTypeAheadComponent } from 'src/app/shared/components/admin-payter-locations-type-ahead/admin-payter-locations-type-ahead.component';
import { PtsSessionOverviewStateService } from './pts-session.state.service';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import {
  CardComponent,
  ExpandListComponent,
  ExpandListConfiguration,
  ExpandListSort,
  ScreenSizeEnum,
  SnackbarService,
} from 'ok-angular-shared/components';

import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardUtility } from 'src/app/shared/utilities/clipboard-utility';
import { MatIconButton } from '@angular/material/button';
import { RevertReservedAmountDialogComponent } from '../dialogs/revert-reserved-amount-dialog/revert-reserved-amount-dialog.component';
import { TerminalEmulatorDialogComponent } from '../dialogs/terminal-emulator-dialog/terminal-emulator-dialog.component';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { PaymentTerminalActionsComponent } from '../../payment-terminal-actions/payment-terminal-actions.component';
import { PayterTerminalLocation, PtsPaymentTerminalSearchEvent, TerminalSessionResponse } from '../pts-payment-terminal-model';

@Component({
  selector: 'adm-pts-session-overview',
  templateUrl: './pts-session-overview.component.html',
  styleUrl: './pts-session-overview.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    PaymentTerminalActionsComponent,
    AdminPayterLocationsTypeAheadComponent,
    TextSearchComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatIconButton,
    CardComponent,
    ExpandListComponent,
    MatPaginator,
    AsyncPipe,
    DatePipe,
    DecimalPipe,
    JsonPipe,
    DkCurrencyPipe,
    MatTooltipModule,
  ],
  providers: [PtsSessionOverviewStateService],
})
export class PtsSessionOverviewComponent {
  appState = inject(AppState);
  state = inject(PtsSessionOverviewStateService);
  private dialog = inject(MatDialog);
  private paymentTerminalService = inject(PtsPaymentTerminalService);
  private snackbarService = inject(SnackbarService);
  public clipboardUtility = inject(ClipboardUtility);

  public moment = moment;
  public disabledCancelReservationAmountReason =
    "For at kunne frigive reservationsbeløb skal følgende være opfyldt: Sessionen have 'PaymentReserved'-status, Firestore-ladningen må ikke have 'Finished'-status, Der skal være gået 5 minutter siden sidste aktivitet er registreret på sessionen eller på Firestore ladningen";

  currentSort = signal<ExpandListSort<TerminalSessionResponse>>({ column: 'updated', direction: 'desc' });

  updatedColumnTemplate = viewChild<TemplateRef<TerminalSessionResponse>>('updatedColumnTemplate');
  chargingTokenColumnTemplate = viewChild<TemplateRef<TerminalSessionResponse>>('chargingTokenColumnTemplate');
  serialNumberColumnTemplate = viewChild<TemplateRef<TerminalSessionResponse>>('serialNumberColumnTemplate');
  statusColumnTemplate = viewChild<TemplateRef<TerminalSessionResponse>>('statusColumnTemplate');
  reservedAmountColumnTemplate = viewChild<TemplateRef<TerminalSessionResponse>>('reservedAmountColumnTemplate');
  commitAmountColumnTemplate = viewChild<TemplateRef<TerminalSessionResponse>>('commitAmountColumnTemplate');

  constructor() {
    this.moment.locale('da');
  }

  public config = computed<ExpandListConfiguration<TerminalSessionResponse>>(() => {
    this.state.sessions();

    return {
      textLineClamp: 3,
      classFn: (session: TerminalSessionResponse) => (session.highlight?.getValue() ? 'highlight' : ''),
      columns: [
        { field: 'updated', header: 'Opdateret', width: '1fr', sortField: 'updated', displayTemplate: this.updatedColumnTemplate() },
        {
          field: 'chargingToken',
          header: 'ChargingToken / PayterSessionId',
          width: '2fr',
          sortField: 'chargingToken',
          visibleFrom: ScreenSizeEnum.md,
          displayTemplate: this.chargingTokenColumnTemplate(),
        },
        {
          field: 'serialNumber',
          header: 'Serienummer',
          width: '1.5fr',
          sortField: 'serialNumber',
          displayTemplate: this.serialNumberColumnTemplate(),
        },
        { field: 'status', header: 'Status', width: '1fr', sortField: 'status', displayTemplate: this.statusColumnTemplate() },
        {
          field: 'reservedAmountInOere',
          header: 'Reserveret beløb',
          width: '1fr',
          sortField: 'reservedAmountInOere',
          displayTemplate: this.reservedAmountColumnTemplate(),
        },
        {
          field: 'commitAmountInOere',
          header: 'Hævet beløb',
          width: '1fr',
          sortField: 'commitAmountInOere',
          displayTemplate: this.commitAmountColumnTemplate(),
        },
      ],
      actions: [
        {
          icon: 'refresh',
          label: 'Frigiv reservationsbeløb',
          tooltipFn: () => this.disabledCancelReservationAmountReason,
          disabledFn: session => !session?.enabledCancelReservationAmount,
          action: session => session && this.revertReservationAmount(session),
        },
      ],
    };
  });

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.ToFirstPage({
        query: value && value.length > 0 ? value : null,
      });
    });
  };

  locationSelected = (location: PayterTerminalLocation | null) => {
    this.state.ToFirstPage({
      terminalSerialNumber: location?.serialNumber,
    });
  };

  public resetDates() {
    this.state.ToFirstPage({
      startDate: null,
      endDate: null,
    });
  }

  public pageSwitch(event: PageEvent) {
    this.state.updatePage(event);
  }

  public onSortChange(sort: ExpandListSort<TerminalSessionResponse>) {
    this.currentSort.set(sort);

    const matSort: Sort = {
      active: sort.column as string,
      direction: sort.direction,
    };

    this.state.updateSort(matSort);
  }

  public async refreshAfterUpdate(terminalSession: TerminalSessionResponse) {
    this.state.triggerRefresh();
  }

  public revertReservationAmount(terminalSession: TerminalSessionResponse) {
    const dialogRef = this.dialog.open(RevertReservedAmountDialogComponent, {
      data: { terminalSession },
      width: '768px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.refreshAfterUpdate(terminalSession);
      }
    });
  }

  public async terminalEmulator(terminalSession: TerminalSessionResponse) {
    const searchEvent = new PtsPaymentTerminalSearchEvent();
    searchEvent.query = terminalSession.serialNumber;
    const paymentTerminal = (await firstValueFrom(this.paymentTerminalService.getPaymentTerminals(searchEvent))).items[0];

    if (paymentTerminal) {
      this.dialog.open(TerminalEmulatorDialogComponent, {
        data: { paymentTerminal },
        width: '1024px',
        minHeight: '650px',
      });
    } else {
      this.snackbarService.warning(`No terminal found with serial number: ${terminalSession.serialNumber}`);
    }
  }

  // Excludes the BehaviorSubject `highlight` field, which the json pipe can't serialize once subscribed.
  public sessionDetails(terminalSession: TerminalSessionResponse) {
    const { highlight, ...rest } = terminalSession;
    return rest;
  }
}
